import * as React from 'react';
import styles from './index.less';
import Sider from './Sider';
import * as AHooks from 'ahooks';
import { connect } from 'dva';
import { Dispatch } from 'redux';
import { FServiceAPI, FUtil } from '@freelog/tools-lib';
import {
  ChangeAction,
  OnMount_Page_Action,
  OnUnmount_Page_Action,
  Version_FetchDataSourceAction,
} from '@/models/collectionManager';
import FSimpleBar from '@/components/FSimpleBar';
import {
  FPodcastRssImportingBanner,
  FPodcastRssSyncErrorBanner,
  FPodcastRssSyncErrorModal,
  resolveRssSyncBannerState,
  buildRssSyncErrorMessages,
  RssSyncProgressData,
} from '@/components/FPodcastRssSubmit';
import { isRssSyncImporting } from '@/utils/rss';
import { Outlet, useLocation, useParams } from 'umi';
import { ConnectState, CollectionManagerState } from '@/models/connect';

const RSS_SYNC_POLL_MS = 60 * 1000;

function getCollectionSidebarResourceID(pathname: string, paramId?: string): string {
  if (paramId) return paramId;
  const matched = pathname.match(
    /\/collectionSidebar\/(?:info|policy|contract|dependency|versionInfo)\/([^/?#]+)/,
  );
  return matched?.[1] ?? '';
}

interface SidebarProps {
  dispatch: Dispatch;
  collectionManager: CollectionManagerState;
}

function Sidebar({ dispatch, collectionManager }: SidebarProps) {
  AHooks.useMount(() => {
    dispatch<OnMount_Page_Action>({
      type: 'collectionManager/onMount_Page',
    });
  });

  AHooks.useUnmount(() => {
    dispatch<OnUnmount_Page_Action>({
      type: 'collectionManager/onUnmount_Page',
    });
  });

  // 五个子页（info / policy / contract / dependency / versionInfo）共用同一布局与顶部状态栏
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const resourceID = getCollectionSidebarResourceID(location.pathname, params.id);

  const [$syncData, set$syncData, get$syncData] =
    FUtil.Hook.useGetState<RssSyncProgressData | null>(null);
  const [$errorModalOpen, set$errorModalOpen] = React.useState<boolean>(false);
  const prevImportingRef = React.useRef<boolean>(false);
  const bannerState = resolveRssSyncBannerState($syncData);

  const fetchSyncProgress = React.useCallback(async () => {
    if (!resourceID) {
      set$syncData(null);
      dispatch<ChangeAction>({
        type: 'collectionManager/change',
        payload: { rss_syncProgress: null },
      });
      return;
    }
    const { ret, errCode, data } = await FServiceAPI.Rss.getSyncProgress({
      resourceId: resourceID,
    });
    if (ret !== 0 || errCode !== 0 || !data) {
      set$syncData(null);
      dispatch<ChangeAction>({
        type: 'collectionManager/change',
        payload: { rss_syncProgress: null },
      });
      return;
    }
    const next = data as RssSyncProgressData;
    const wasImporting = prevImportingRef.current;
    const nowImporting = isRssSyncImporting(next.status);
    prevImportingRef.current = nowImporting;
    set$syncData(next);
    dispatch<ChangeAction>({
      type: 'collectionManager/change',
      payload: { rss_syncProgress: next },
    });
    // 导入从进行中变为结束时刷新单品列表与侧栏封面
    if (wasImporting && !nowImporting && collectionManager.version_resourceID === resourceID) {
      dispatch<Version_FetchDataSourceAction>({
        type: 'collectionManager/version_fetchDataSource',
      });
      dispatch({
        type: 'collectionManager/sider_onUpdate_Data',
      });
    }
  }, [resourceID, dispatch, collectionManager.version_resourceID]);

  React.useEffect(() => {
    prevImportingRef.current = false;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    (async () => {
      if (cancelled) return;
      await fetchSyncProgress();
      if (cancelled) return;
      // 需求：一分钟轮询一次；导入中同样按此频率更新横幅与列表
      timer = setInterval(() => {
        fetchSyncProgress();
      }, RSS_SYNC_POLL_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [resourceID, fetchSyncProgress]);

  // 单品管理页触发「同步更新」后立即拉取进度（见 custom event）
  // 先标记为 importing，避免同步很快结束时 wasImporting 从未为 true 而漏刷新
  React.useEffect(() => {
    const onRssSyncRequested = () => {
      prevImportingRef.current = true;
      fetchSyncProgress();
    };
    window.addEventListener('freelog:rss-sync-requested', onRssSyncRequested);
    return () => {
      window.removeEventListener('freelog:rss-sync-requested', onRssSyncRequested);
    };
  }, [fetchSyncProgress]);

  return (
    <div className={styles.root}>
      {bannerState.kind === 'importing' && <FPodcastRssImportingBanner />}
      {bannerState.kind === 'multiple' && (
        <FPodcastRssSyncErrorBanner
          mode='multiple'
          onView={() => set$errorModalOpen(true)}
        />
      )}
      {bannerState.kind === 'sync_failed' && (
        <FPodcastRssSyncErrorBanner mode='sync_failed' />
      )}
      {bannerState.kind === 'single_item' && (
        <FPodcastRssSyncErrorBanner
          mode='single_item'
          itemTitle={bannerState.itemTitle}
          errorMessage={bannerState.errorMessage}
        />
      )}
      <div className={styles.leftRight}>
        <div className={styles.Slider}>
          <div>
            <Sider />
          </div>
        </div>
        <div className={styles.rightContent}>
          <FSimpleBar height={'100%'}>
            <div className={styles.rightContent_inner}>
              <Outlet />
            </div>
          </FSimpleBar>
        </div>
      </div>

      <FPodcastRssSyncErrorModal
        open={$errorModalOpen}
        onClose={() => set$errorModalOpen(false)}
        messages={buildRssSyncErrorMessages(get$syncData())}
      />
    </div>
  );
}

export default connect(({ collectionManager }: ConnectState) => ({
  collectionManager,
}))(Sidebar);
